import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CreateMonitoring } from '../../services/create-monitoring';
import { ProductSelectMonitoring } from '../../sections/product-select/product-select';
import { MonitoringDefine } from '../../sections/monitoring-define/monitoring-define';
import { MonitoringRequirements } from '../../sections/monitoring-requirements/monitoring-requirements';
import { MonitoringWindow } from '../../sections/monitoring-window/monitoring-window';
import { MonitoringReview } from '../../sections/monitoring-review/monitoring-review';
import { MonitoringCreators } from '../../sections/monitoring-creators/monitoring-creators';

type WizardStepId = 'product' | 'define' | 'requirements' | 'window' | 'review' | 'creators';

interface WizardStep {
  id: WizardStepId;
  label: string;
  sub: string;
  eyeword: string;
  title: string;
  desc: string;
}

interface ChecklistItem {
  label: string;
  ok: boolean;
}

@Component({
  selector: 'cmb-wizard-first',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductSelectMonitoring,
    MonitoringDefine,
    MonitoringRequirements,
    MonitoringWindow,
    MonitoringReview,
    MonitoringCreators,
  ],
  templateUrl: './wizard-first-monitoring.html',
  styleUrl: './wizard-first-monitoring.scss',
})
export class WizardFirstMonitoring {

  public readonly createMonitoringService = inject(CreateMonitoring);

  readonly launch = output<void>();

  private readonly baseSteps: WizardStep[] = [
    { id: 'product', label: 'Product', sub: 'What you are watching', eyeword: 'Product', title: 'Which product is this for?', desc: 'Pick the product this monitoring brief is about. You can leave it blank if the watch is brand-wide.' },
    { id: 'define', label: 'Define the brief', sub: 'Name and what to watch', eyeword: 'Define', title: 'What should we watch for?', desc: 'Name the brief, choose the kind of monitoring, and describe the content you want flagged.' },
    { id: 'requirements', label: 'Requirements', sub: 'Document or written rules', eyeword: 'Requirements', title: 'How should we judge the content?', desc: 'Upload a brief document, or write the requirements and the things that are not allowed. Add compliance docs if they apply.' },
    { id: 'window', label: 'Window', sub: 'When to watch', eyeword: 'Window', title: 'When should monitoring run?', desc: 'Choose when to start watching and how long to keep watching. You can start up to 30 days back, and watch for up to a year.' },
    { id: 'review', label: 'Review', sub: 'Check the brief', eyeword: 'Review', title: 'Check your brief', desc: 'Give it a final look before you choose who to monitor.' },
    { id: 'creators', label: 'Add creators', sub: 'Who to watch', eyeword: 'Creators', title: 'Who should we monitor?', desc: 'Add the creators you want this brief to watch — four ways, your pick.' },
  ];

  readonly steps = computed(() => this.baseSteps);
  readonly current = signal<number>(0);
  readonly step = computed(() => this.steps()[this.current()]);
  readonly eyebrow = computed(() => `Step ${this.current() + 1} · ${this.step().eyeword}`);
  readonly progressPct = computed(() => (this.current() / (this.steps().length - 1)) * 100);

  /** Advisory only — these do not block navigation. */
  readonly checklist = computed<ChecklistItem[]>(() => {
    const service = this.createMonitoringService;
    const hasDocument = service.requirementsDocumentUrl().trim().length > 0;
    const hasText = service.briefTextHtml().trim().length > 0
      || service.requirementsHtml().trim().length > 0;
    return [
      { label: 'Name', ok: service.basicInfoModel().briefTitle.trim().length > 0 },
      { label: 'Monitor type', ok: !!service.monitorType() },
      { label: 'Document or brief text', ok: hasDocument || hasText },
    ];
  });

  readonly essentialsDone = computed(() => this.checklist().filter((item) => item.ok).length);

  readonly primaryLabel = computed(() => (this.step().id === 'creators' ? 'Submit brief' : 'Continue'));

  private readonly scrollArea = viewChild<ElementRef<HTMLElement>>('scrollArea');

  constructor() {
    effect(() => {
      this.current();
      this.scrollArea()?.nativeElement.scrollTo({ top: 0 });
    });
  }

  isDone(index: number): boolean {
    return index < this.current();
  }

  stepCircleClass(index: number): string {
    if (index === this.current()) {
      return 'border-brand-studio bg-brand-studio text-white';
    }
    if (this.isDone(index)) {
      return 'border-brand-studio bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio';
    }
    return 'border-border text-muted-foreground';
  }

  goTo(index: number): void {
    this.current.set(index);
  }

  back(): void {
    if (this.current() > 0) {
      this.current.update((value) => value - 1);
    }
  }

  next(): void {
    if (this.current() < this.steps().length - 1) {
      this.current.update((value) => value + 1);
    }
  }

  primary(): void {
    if (this.step().id === 'creators') {
      this.launch.emit();
      return;
    }
    this.next();
  }
}
